import {
  PersonalizedProductDetailsResponse,
  ProductPriceObj,
} from 'api/product/getPersonalizedProductDetails/types';
import cn from 'classnames';
import Price from 'components/price';
import { OrderType } from 'constants/order';
import { StaticPDPProduct, Variant } from 'modules/product-details/types';
import React from 'react';

export interface PriceElementProps {
  className?: string;
  couponPrices: ProductPriceObj | undefined;
  hasInfoPrice?: boolean;
  oneOffPrice?: number;
  orderType: OrderType;
  price?: number;
  pricePerKilo?: number;
  product?: StaticPDPProduct;
  selectedVariant?: Variant | undefined;
  selectedVariantDiscount: PersonalizedProductDetailsResponse['variants'][number];
  size?: 'small' | 'large' | 'xs';
  tags?: Array<string>;
  showComparePrice?: boolean;
}

const PriceElement: React.FC<PriceElementProps> = ({
  className,
  couponPrices,
  orderType,
  pricePerKilo,
  product,
  selectedVariantDiscount,
  size = 'large',
  tags,
  showComparePrice = true,
}) => {
  if (!couponPrices) {
    return null;
  }

  const couponName = couponPrices.coupon?.code;
  const discountPercentage = couponPrices.coupon?.percentage;

  const compareAtPrice: number = Number(
    orderType === OrderType.ONE_OFF
      ? selectedVariantDiscount.one_off.compare?.before ||
          selectedVariantDiscount.one_off.price
      : selectedVariantDiscount.subscription?.compare.before
  );

  const discountedPrice: number = Number(
    orderType === OrderType.ONE_OFF
      ? selectedVariantDiscount.one_off.compare?.after ||
          selectedVariantDiscount.one_off.price
      : selectedVariantDiscount.subscription?.compare.after
  );

  const hasCouponDiscount = discountPercentage && couponName && discountedPrice;

  return (
    <>
      <div className={cn('h-14 ', !hasCouponDiscount && 'mt-2', className)}>
        <div className="flex flex-col items-end justify-end md:text-3xl">
          {hasCouponDiscount ? (
            <>
              <span className="text-sm leading-[14px] text-text-secondary line-through">
                <Price value={Number(compareAtPrice)} />
              </span>
              <span
                className={cn(
                  'font-black text-text-light-bg-discounted-price',
                  size === 'xs' && 'text-base',
                  size === 'small' && 'text-lg',
                  size === 'large' && 'text-2xl'
                )}
              >
                <Price value={Number(discountedPrice)} />
              </span>
            </>
          ) : (
            <>
              {showComparePrice && compareAtPrice > discountedPrice && (
                <span className="text-sm leading-[14px] text-text-secondary line-through">
                  <Price value={Number(compareAtPrice)} />
                </span>
              )}
              <span
                className={cn(
                  'font-black leading-[30px]',
                  size === 'xs' && 'text-base',
                  size === 'small' && 'text-lg',
                  size === 'large' && 'text-2xl',
                  compareAtPrice > discountedPrice &&
                    'text-text-light-bg-discounted-price'
                )}
              >
                <Price value={Number(discountedPrice)} />
              </span>
            </>
          )}
          {((product && !product.tags.includes('KGP')) ||
            (tags && !tags.includes('KGP'))) &&
          pricePerKilo ? (
            <span className="text-sm leading-3 text-text-secondary">
              <Price value={pricePerKilo} />
              /kg
            </span>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default PriceElement;
